import type { ToolCallContent } from "@tsmono/inspect-common/types";
import { IconName } from "@tsmono/react/icons";
import { isRecord, nullProtoRecord } from "@tsmono/util";

export const kToolTodoContentType = "agent/todo-list";

/* Per-tool header icons for the tool block grammar. Keyed by the tool names
   Inspect and the common CLI agents emit. */
const kToolIcons = nullProtoRecord(
  new Map<string, IconName>(
    Object.entries({
      // shells
      bash: "terminal",
      Bash: "terminal",
      bash_session: "terminal",
      shell: "terminal",
      shell_command: "terminal",
      exec_command: "terminal",
      // code
      python: "codeSquare",
      code_execution: "codeSquare",
      exec: "codeSquare",
      // web
      web_search: "search",
      WebSearch: "search",
      web_fetch: "globe",
      WebFetch: "globe",
      web_browser: "window",
      // editing / files
      text_editor: "edit",
      str_replace_editor: "edit",
      str_replace_based_edit_tool: "edit",
      Edit: "edit",
      Write: "edit",
      NotebookEdit: "edit",
      Read: "fileText",
      Glob: "search",
      Grep: "search",
      // sub-agents
      task: "people",
      Task: "people",
      agent: "people",
      Agent: "people",
      spawn_agent: "people",
      send_input: "people",
      wait_agent: "people",
      resume_agent: "people",
      close_agent: "people",
      agent_status: "people",
      agent_wait: "people",
      agent_cancel: "people",
      agent_list: "people",
      // planning
      TodoWrite: "listCheck",
      todo_write: "listCheck",
      update_plan: "listCheck",
      think: "lightbulb",
      // misc
      computer: "computer",
      tool_search: "search",
      mcp_list_tools: "plug",
      mcp_call: "plug",
    } satisfies Record<string, IconName>)
  )
);

/**
 * Header icon for a tool call. Unknown client tools get the general tool
 * icon; unknown server tools keep the globe (the server tell).
 */
export const iconForTool = (
  tool: string,
  options?: { server?: boolean }
): IconName => {
  const icon = kToolIcons[tool];
  if (icon) {
    return icon;
  }
  if (tool.startsWith("browser_")) {
    return "window";
  }
  return options?.server ? "globe" : "tools";
};

export interface ToolCallResult {
  name: string;
  functionCall: string;
  /** Optional descriptive title (e.g. "task: general") for use in panel headers. */
  title?: string;
  input?: unknown;
  description?: string;
  contentType?: string;
}

/**
 * Resolves the input and metadata for a given tool call.
 */
export const resolveToolInput = (
  fn: string,
  toolArgs: Record<string, unknown>
): ToolCallResult => {
  const toolName = fn;

  const inputDescriptor = extractInputMetadata(toolName, toolArgs);
  const { input, description, args } = extractInput(toolArgs, inputDescriptor);
  const functionCall =
    args.length > 0 ? `${toolName}(${args.join(", ")})` : toolName;

  // For subagent-dispatch tools, use the subagent_type as the display name.
  // "Task"/"task" predate Claude Code v2.1.70 and Inspect deepagent's rename;
  // "Agent"/"agent" are the post-rename names.
  if (
    (fn === "Task" || fn === "task" || fn === "Agent" || fn === "agent") &&
    typeof toolArgs.subagent_type === "string"
  ) {
    const subagentType = toolArgs.subagent_type;
    return {
      name: fn,
      functionCall: `${fn}: ${subagentType}`,
      title: `${fn}: ${subagentType}`,
      input,
      description,
      contentType: "markdown",
    };
  }

  // Codex CLI's sub-agent spawn tool — mirror the Task/Agent treatment, keyed
  // on `agent_type` (Codex's analog of `subagent_type`).
  if (fn === "spawn_agent" && typeof toolArgs.agent_type === "string") {
    const agentType = toolArgs.agent_type;
    return {
      name: fn,
      functionCall: `${fn}: ${agentType}`,
      title: `${fn}: ${agentType}`,
      input,
      description,
      contentType: "markdown",
    };
  }

  // Inspect deepagent background lifecycle tools return markdown status blocks
  // (the call-line title is supplied by their server-side ToolCallViewer).
  if (
    fn === "agent_status" ||
    fn === "agent_wait" ||
    fn === "agent_cancel" ||
    fn === "agent_list"
  ) {
    return {
      name: fn,
      functionCall,
      input,
      description,
      contentType: "markdown",
    };
  }

  // Codex CLI's sub-agent management tools return JSON-wrapped status containing
  // the sub-agent's (markdown) final answer; render as markdown so
  // `transformToolOutput` can surface that answer instead of an escaped blob.
  if (fn === "wait_agent" || fn === "close_agent" || fn === "resume_agent") {
    return {
      name: fn,
      functionCall,
      input,
      description,
      contentType: "markdown",
    };
  }

  return {
    name: fn,
    functionCall,
    input,
    description,
    contentType: inputDescriptor?.contentType,
  };
};

interface ToolInputDescriptor {
  inputArg?: string | string[];
  descriptionArg?: string;
  contentType?: string;
  inputToStr?: (input: unknown) => string | undefined;
}

const extractInputMetadata = (
  toolName: string,
  toolArgs: Record<string, unknown>
): ToolInputDescriptor | undefined => {
  // `text_editor` create writes a whole file — show its `file_text` as the
  // body (highlighted by the target file's type) instead of an inline arg.
  if (toolName === "text_editor" && toolArgs.command === "create") {
    return {
      inputArg: "file_text",
      contentType: prismLanguageForPath(toolArgs.path),
    };
  }
  if (toolName === "bash") {
    return {
      inputArg: ["cmd", "command"],
      contentType: "bash",
    };
  } else if (toolName === "python") {
    return {
      inputArg: "code",
      contentType: "python",
    };
  } else if (toolName === "web_search") {
    return {
      inputArg: "query",
      contentType: "json",
    };
  } else if (toolName === "Bash") {
    return {
      inputArg: "command",
      descriptionArg: "description",
      contentType: "bash",
    };
  } else if (toolName === "shell_command") {
    return {
      inputArg: "command",
      descriptionArg: "description",
      contentType: "bash",
    };
  } else if (toolName === "shell") {
    return {
      inputArg: "command",
      contentType: "bash",
      inputToStr: (input: unknown) => {
        if (Array.isArray(input)) {
          return input.join(" ");
        }
        return undefined;
      },
    };
  } else if (toolName == "TodoWrite") {
    return {
      inputArg: "todos",
      contentType: kToolTodoContentType,
    };
  } else if (toolName == "update_plan") {
    return {
      inputArg: "plan",
      contentType: kToolTodoContentType,
    };
  } else if (toolName == "todo_write") {
    return {
      inputArg: "todos",
      contentType: kToolTodoContentType,
    };
  } else if (toolName === "Task" || toolName === "Agent") {
    return {
      inputArg: "prompt",
      descriptionArg: "description",
      contentType: "markdown",
    };
  } else if (toolName === "task" || toolName === "agent") {
    return {
      inputArg: "prompt",
      descriptionArg: "task_description",
      contentType: "markdown",
    };
  } else if (toolName === "exec_command") {
    // Codex CLI shell tool
    return {
      inputArg: "cmd",
      contentType: "bash",
    };
  } else if (toolName === "exec" && typeof toolArgs.input === "string") {
    // Codex CLI Code Mode: `input` is JavaScript source calling `tools.*` —
    // often hundreds of characters with embedded string literals. Keyed on the
    // arg shape so an unrelated `exec` tool keeps the default rendering.
    return {
      inputArg: "input",
      contentType: "javascript",
    };
  } else if (toolName === "spawn_agent") {
    return {
      inputArg: "message",
      contentType: "markdown",
    };
  } else if (toolName === "send_input") {
    return {
      inputArg: "message",
      contentType: "markdown",
    };
  } else if (toolName === "tool_search") {
    // Codex CLI tool-catalog search. Leave the input (query) to the default
    // function-call rendering; `contentType: "markdown"` only routes the
    // *output* (the catalog) through the markdown renderer, where
    // transformToolOutput has reshaped it into a compact namespace/signature list.
    return {
      contentType: "markdown",
    };
  } else {
    return undefined;
  }
};

// Map a file path to a registered Prism language for body highlighting; returns
// undefined (plain text) for extensions Prism isn't loaded for.
const prismLanguageForPath = (path: unknown): string | undefined => {
  if (typeof path !== "string") {
    return undefined;
  }
  const ext = path.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "py":
      return "python";
    case "js":
    case "jsx":
    case "mjs":
    case "cjs":
      return "javascript";
    case "json":
      return "json";
    case "sh":
    case "bash":
      return "bash";
    case "yaml":
    case "yml":
      return "yaml";
    default:
      return undefined;
  }
};

const kCodexAgentResultTools = new Set([
  "wait_agent",
  "close_agent",
  "resume_agent",
]);

/**
 * Reshapes a Codex sub-agent tool *result* into markdown, or returns undefined
 * when no reshape applies (caller renders the raw output).
 *
 * `spawn_agent` returns `{"agent_id": …, "nickname": …}` — we surface a compact
 * line naming the spawned sub-agent. `wait_agent` / `close_agent` /
 * `resume_agent` return JSON-wrapped status whose `completed` fields hold the
 * sub-agent's markdown final answer — we surface those answers. Accepts a raw
 * string output or structured content (the viewer wraps tool results as
 * `Content[]`), extracting the underlying text either way. The raw output
 * remains in the JSON tab.
 *
 * (`tool_search` is handled separately by `parseToolSearchCatalog` +
 * `ToolSearchView`, which needs real collapsible `<details>` elements that the
 * HTML-escaping markdown pipeline can't produce from a string.)
 */
export const codexToolMarkdown = (
  fn: string,
  output: unknown
): string | undefined => {
  const text = toolOutputText(output);
  if (text === undefined) {
    return undefined;
  }
  if (fn === "spawn_agent") {
    return formatSpawnAgentResult(text);
  }
  if (kCodexAgentResultTools.has(fn)) {
    return extractCodexAgentAnswers(text);
  }
  return undefined;
};

// Reshape a `spawn_agent` result (`{"agent_id": …, "nickname": …}`) into a
// compact markdown line: the nickname, then the agent id in code font.
const formatSpawnAgentResult = (text: string): string | undefined => {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (!isRecord(data)) {
    return undefined;
  }
  const record = data;
  const nickname =
    typeof record.nickname === "string" ? record.nickname : undefined;
  const agentId =
    typeof record.agent_id === "string" ? record.agent_id : undefined;
  const parts: string[] = [];
  if (nickname) {
    parts.push(nickname);
  }
  if (agentId) {
    parts.push(`\`${agentId}\``);
  }
  return parts.length > 0 ? parts.join(" — ") : undefined;
};

// Extract the underlying text from a tool output that may be a raw string or
// structured content (text items, optionally wrapped in a `tool` content item).
export const toolOutputText = (output: unknown): string | undefined => {
  if (typeof output === "string") {
    return output;
  }
  if (Array.isArray(output)) {
    const parts: string[] = [];
    for (const item of output) {
      collectContentText(item, parts);
    }
    return parts.length > 0 ? parts.join("") : undefined;
  }
  return undefined;
};

const collectContentText = (item: unknown, parts: string[]): void => {
  if (!isRecord(item)) {
    return;
  }
  const record = item;
  if (record.type === "text" && typeof record.text === "string") {
    parts.push(record.text);
  } else if (record.type === "tool" && Array.isArray(record.content)) {
    for (const child of record.content) {
      collectContentText(child, parts);
    }
  }
};

// Codex injects `<subagent_notification>{…}</subagent_notification>` as a user
// message to tell a parent that a sub-agent finished. The `completed` answer is
// already surfaced by the paired wait_agent/close_agent result, so we collapse
// the (duplicated, raw-JSON) notification to a one-line status:
// `agent <status>: <agent_path>`. Returns undefined when no notification is
// present (caller renders the message normally); the raw text stays in the JSON
// tab. Accepts a string or structured content.
export const formatSubagentNotifications = (
  content: unknown
): string | undefined => {
  const text = toolOutputText(content);
  if (text === undefined || !text.includes("<subagent_notification>")) {
    return undefined;
  }
  const lines: string[] = [];
  const regex = /<subagent_notification>([\s\S]*?)<\/subagent_notification>/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const line = formatSubagentNotification(match[1]!);
    if (line) {
      lines.push(line);
    }
  }
  return lines.length > 0 ? lines.join("\n\n") : undefined;
};

const formatSubagentNotification = (payload: string): string | undefined => {
  let data: unknown;
  try {
    data = JSON.parse(payload.trim());
  } catch {
    return undefined;
  }
  if (!isRecord(data)) {
    return undefined;
  }
  const record = data;
  const agentPath =
    typeof record.agent_path === "string" ? record.agent_path : undefined;
  const status =
    record.status && typeof record.status === "object"
      ? Object.keys(record.status)[0]
      : undefined;
  if (agentPath === undefined && status === undefined) {
    return undefined;
  }
  const statusWord = status ?? "notified";
  return agentPath
    ? `agent ${statusWord}: \`${agentPath}\``
    : `agent ${statusWord}`;
};

export interface ToolSearchToolEntry {
  /** `name(param1, param2, …)` */
  signature: string;
  /** dedented description (markdown), or "" when absent */
  description: string;
}

export interface ToolSearchNamespaceEntry {
  name?: string;
  description: string;
  tools: ToolSearchToolEntry[];
}

// Parse Codex's `tool_search` result into a structured form for `ToolSearchView`
// to render (each tool as a collapsible <details>). The result is a serde-tagged
// `ToolSpec` list whose entries are either `{type:"namespace", tools:[…]}` or a
// bare `{type:"function", name, parameters, …}` (e.g. `read_file`) — the latter
// are gathered into a single nameless group so their signatures still render.
// Returns undefined (pass through to default rendering) if the shape is
// unexpected. Accepts a raw string or structured content (Content[]).
export const parseToolSearchCatalog = (
  output: unknown
): ToolSearchNamespaceEntry[] | undefined => {
  const text = toolOutputText(output);
  if (text === undefined) {
    return undefined;
  }
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (!Array.isArray(data)) {
    return undefined;
  }

  const namespaces: ToolSearchNamespaceEntry[] = [];
  const looseTools: ToolSearchToolEntry[] = [];
  for (const entry of data) {
    if (!isRecord(entry)) {
      continue;
    }
    const record = entry;
    const rawTools = Array.isArray(record.tools) ? record.tools : undefined;

    // A top-level function tool (no nested `tools`) — render it directly rather
    // than mistaking it for an empty namespace that drops the signature.
    if (
      record.type === "function" ||
      (rawTools === undefined && "parameters" in record)
    ) {
      const tool = parseToolEntry(record);
      if (tool) {
        looseTools.push(tool);
      }
      continue;
    }

    const name = typeof record.name === "string" ? record.name : undefined;
    const description =
      typeof record.description === "string" ? record.description : "";
    const toolList = rawTools ?? [];
    if (name === undefined && toolList.length === 0) {
      continue;
    }

    const tools: ToolSearchToolEntry[] = [];
    for (const tool of toolList) {
      if (!isRecord(tool)) {
        continue;
      }
      const parsed = parseToolEntry(tool);
      if (parsed) {
        tools.push(parsed);
      }
    }
    namespaces.push({ name, description, tools });
  }

  if (looseTools.length > 0) {
    namespaces.push({ name: undefined, description: "", tools: looseTools });
  }

  return namespaces.length > 0 ? namespaces : undefined;
};

// Parse a single function entry (`{name, parameters, description}`) into a
// `name(param, …)` signature + dedented description.
const parseToolEntry = (
  tool: Record<string, unknown>
): ToolSearchToolEntry | undefined => {
  const name = typeof tool.name === "string" ? tool.name : undefined;
  if (name === undefined) {
    return undefined;
  }
  return {
    signature: `${name}(${toolParamNames(tool).join(", ")})`,
    description:
      typeof tool.description === "string"
        ? dedentDescription(tool.description)
        : "",
  };
};

const toolParamNames = (tool: Record<string, unknown>): string[] => {
  const parameters = tool.parameters;
  if (isRecord(parameters)) {
    const properties = parameters.properties;
    if (isRecord(properties)) {
      return Object.keys(properties);
    }
  }
  return [];
};

// Dedent each line: the source descriptions mix 8-space-indented lines with
// flush-left markdown, and the indented lines would otherwise render as code
// blocks. (No HTML escaping needed — these render via MarkdownDiv, which escapes
// HTML, so `<...>` placeholders show literally and markdown structure is kept.)
const dedentDescription = (description: string): string =>
  description
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .trim();

const extractCodexAgentAnswers = (output: unknown): string | undefined => {
  if (typeof output !== "string") {
    return undefined;
  }
  let data: unknown;
  try {
    data = JSON.parse(output);
  } catch {
    return undefined;
  }

  const answers: string[] = [];
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(visit);
    } else if (node && typeof node === "object") {
      for (const [key, value] of Object.entries(node)) {
        if (
          (key === "completed" || key === "failed" || key === "cancelled") &&
          typeof value === "string"
        ) {
          answers.push(cleanCodexAnswer(value));
        } else {
          visit(value);
        }
      }
    }
  };
  visit(data);

  const nonEmpty = answers.filter((answer) => answer.length > 0);
  return nonEmpty.length > 0 ? nonEmpty.join("\n\n---\n\n") : undefined;
};

// Strip one valid trailing Codex bookkeeping envelope from the rendered
// projection. Arbitrary or malformed tag-like answer text remains evidence.
const cleanCodexAnswer = (text: string): string => {
  const match =
    /<content-internal>([A-Za-z0-9+/]+={0,2})<\/content-internal>\s*$/.exec(
      text
    );
  const payload = match?.[1];
  if (!match || payload === undefined) {
    return text;
  }

  const visibleAnswer = text.slice(0, match.index);
  if (
    visibleAnswer.includes("<content-internal>") ||
    visibleAnswer.includes("</content-internal>")
  ) {
    return text;
  }

  try {
    const decoded = Uint8Array.from(atob(payload), (char) =>
      char.charCodeAt(0)
    );
    JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(decoded));
  } catch {
    return text;
  }

  return visibleAnswer.trimEnd();
};

/**
 * Substitutes `{{param_name}}` placeholders in tool call content
 * with actual values from the tool call arguments.
 */
export const substituteToolCallContent = (
  content: ToolCallContent,
  args: Record<string, unknown>
): ToolCallContent => {
  const replace = (text: string): string =>
    text.replace(/\{\{(\w+)\}\}/g, (match, key: string) =>
      Object.hasOwn(args, key) ? String(args[key]) : match
    );
  return {
    ...content,
    title: content.title ? replace(content.title) : content.title,
    content: replace(content.content),
  };
};

const extractInput = (
  args: Record<string, unknown>,
  inputDescriptor?: ToolInputDescriptor
): { input?: unknown; description?: string; args: string[] } => {
  const formatArg = (key: string, value: unknown) => {
    const quotedValue =
      value === null
        ? "None"
        : typeof value === "string"
          ? `"${value}"`
          : typeof value === "object" || Array.isArray(value)
            ? JSON.stringify(value, undefined, 2)
            : // Remaining cases are number | boolean | bigint | symbol | undefined
              // — all safe for String().
              // eslint-disable-next-line @typescript-eslint/no-base-to-string
              String(value);
    return `${key}: ${quotedValue}`;
  };

  // Use the input descriptor to snip apart args
  if (inputDescriptor) {
    const filterKeys = new Set<string>();
    const base: { input?: unknown; description?: string } = {};

    if (inputDescriptor.inputArg) {
      // Support string | string[] for inputArg
      const inputArgs = Array.isArray(inputDescriptor.inputArg)
        ? inputDescriptor.inputArg
        : [inputDescriptor.inputArg];

      // Find the first matching arg key
      const matchedKey = inputArgs.find((key) => args[key] !== undefined);
      if (matchedKey) {
        filterKeys.add(matchedKey);
        base.input = inputDescriptor.inputToStr
          ? inputDescriptor.inputToStr(args[matchedKey]) || args[matchedKey]
          : args[matchedKey];
      }

      // Filter all candidate keys from the display args
      for (const key of inputArgs) {
        filterKeys.add(key);
      }
    }

    if (
      inputDescriptor.descriptionArg &&
      args[inputDescriptor.descriptionArg]
    ) {
      filterKeys.add(inputDescriptor.descriptionArg);
      base.description = String(args[inputDescriptor.descriptionArg]);
    }

    const filteredArgs = Object.keys(args)
      .filter((key) => {
        return !filterKeys.has(key);
      })
      .map((key) => {
        return formatArg(key, args[key]);
      });

    return {
      ...base,
      args: filteredArgs,
    };
  } else {
    const formattedArgs = Object.keys(args).map((key) => {
      return formatArg(key, args[key]);
    });

    return {
      args: formattedArgs,
    };
  }
};
